import classNames from 'classnames';
import {
  CardSuit,
  ICard,
  suitToNamed,
  valueToNamed,
  valueToRank,
} from '../../Models/CardModels';
import { IUnrevealedCard } from '../../Models/StateModels';
import Card from '../General/Card/Card';

export interface IOpponentTableCardsProps {
  visibleCards: ICard[];
  hiddenCards: IUnrevealedCard[];
}

const CARD_WIDTH = 26;
const BACK_OFFSET = 2;
const SLOT_WIDTH = CARD_WIDTH + BACK_OFFSET;
const MAX_CARD_STEP = 29;
const MAX_ROW_WIDTH = 86;

const CARD_SIZE_CLASS_NAME =
  'w-[26px] h-[36px] rounded-[4px] outline-1 -outline-offset-1 outline-black/25';

const SUIT_SYMBOLS: Record<CardSuit, string> = {
  [CardSuit.HEARTS]: '♥︎',
  [CardSuit.DIAMONDS]: '♦︎',
  [CardSuit.CLUBS]: '♣︎',
  [CardSuit.SPADES]: '♠︎',
};

const isRedSuit = (suit: CardSuit) =>
  suit === CardSuit.HEARTS || suit === CardSuit.DIAMONDS;

function IndexTile({ card }: { card: ICard }) {
  return (
    <span
      role="img"
      aria-label={`${valueToNamed(card.value)} of ${suitToNamed(card.suit)}`}
      className={classNames(
        CARD_SIZE_CLASS_NAME,
        'flex flex-col items-center justify-center gap-px bg-white leading-none font-bold shadow-seat-card',
        isRedSuit(card.suit) ? 'text-suit-red' : 'text-suit-black'
      )}
    >
      <span aria-hidden="true" className="text-[15px] tracking-[-0.03em]">
        {valueToRank(card.value)}
      </span>
      <span aria-hidden="true" className="text-[13px]">
        {SUIT_SYMBOLS[card.suit]}
      </span>
    </span>
  );
}

export default function OpponentTableCards({
  visibleCards,
  hiddenCards,
}: IOpponentTableCardsProps) {
  const slotCount = Math.max(visibleCards.length, hiddenCards.length);
  if (slotCount === 0) {
    return null;
  }

  const cardStep = Math.min(
    MAX_CARD_STEP,
    (MAX_ROW_WIDTH - SLOT_WIDTH) / Math.max(slotCount - 1, 1)
  );
  const rowWidth = (slotCount - 1) * cardStep + SLOT_WIDTH;
  const slots = Array.from({ length: slotCount }, (_, i) => i);

  return (
    <div
      className="pointer-events-none relative h-[38px]"
      style={{ width: rowWidth }}
    >
      {slots.flatMap((i) => [
        hiddenCards[i] && (
          <div
            key={hiddenCards[i].cardId}
            className="absolute top-[2px] opacity-85"
            style={{ left: i * cardStep + BACK_OFFSET }}
          >
            <Card
              card={hiddenCards[i]}
              className={classNames(CARD_SIZE_CLASS_NAME, 'block max-w-none')}
            />
          </div>
        ),
        visibleCards[i] && (
          <div
            key={visibleCards[i].cardId}
            className="absolute top-0"
            style={{ left: i * cardStep }}
          >
            <IndexTile card={visibleCards[i]} />
          </div>
        ),
      ])}
    </div>
  );
}
